import React from 'react';
import { StatusPill, PriorityPill, PlatformIcon, Avatar, AvatarStack, DueBadge } from './mccUi';
import { statusTone, humanDate } from './mccConstants';
import { MessageSquare, Film, Users } from 'lucide-react';

/**
 * A compact media-first project/idea card. Used across dashboards, kanban and lists.
 *
 * Props:
 *   item: any doc from mcc_projects / mcc_ideas / mcc_requests
 *   variant: 'project' | 'idea' | 'request'
 *   nameOf: (uid) => name
 *   onClick: open drawer
 */
export default function MccContentCard({ item, variant = 'project', nameOf = (x) => x, onClick, testid }) {
  const assignedNames = (item.assigned_to || []).map((uid) => nameOf(uid));
  const platforms = [item.primary_platform, ...(item.additional_platforms || [])].filter(Boolean);
  const commentsCount = (item.comments || []).length;
  const versionsCount = variant === 'project' ? (item.versions || []).length : null;
  const isOverdue = (() => {
    const dd = item.due_date || item.requested_completion_date;
    if (!dd) return false;
    return String(dd).slice(0, 10) < new Date().toISOString().slice(0, 10)
      && !['completed', 'published', 'archived', 'declined'].includes(item.status);
  })();

  const t = statusTone(item.status);

  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rounded-xl border bg-white hover:shadow-md hover:-translate-y-0.5 transition-all p-3 relative overflow-hidden group ${
        isOverdue ? 'border-rose-300 ring-1 ring-rose-100' : 'border-slate-200'
      }`}
    >
      {/* Left status stripe */}
      <span className="absolute top-0 left-0 bottom-0 w-1" style={{ background: t.accent }} />

      <div className="pl-2">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <PriorityPill priority={item.priority} />
              {isOverdue && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 uppercase tracking-wider">Overdue</span>}
            </div>
            <p className="text-sm font-semibold text-slate-900 mt-1 line-clamp-2">{item.title}</p>
            {item.hook ? <p className="text-[11px] text-slate-500 mt-0.5 italic line-clamp-2">&ldquo;{item.hook}&rdquo;</p> : null}
          </div>
          {platforms.length > 0 && (
            <div className="flex -space-x-1 flex-shrink-0">
              {platforms.slice(0, 3).map((p) => (
                <span key={p} className="ring-2 ring-white rounded-full"><PlatformIcon platform={p} size={20} /></span>
              ))}
              {platforms.length > 3 && (
                <span className="ring-2 ring-white rounded-full bg-slate-200 text-slate-700 inline-flex items-center justify-center text-[9px] font-bold" style={{ width: 20, height: 20 }}>+{platforms.length - 3}</span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {assignedNames.length > 0 ? (
              <AvatarStack names={assignedNames} size={20} max={3} />
            ) : (
              <span className="text-[10px] text-slate-400 italic">Unassigned</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            {commentsCount > 0 && (
              <span className="inline-flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{commentsCount}</span>
            )}
            {versionsCount !== null && versionsCount > 0 && (
              <span className="inline-flex items-center gap-0.5"><Film className="w-3 h-3" />{versionsCount}</span>
            )}
            <DueBadge dueIso={item.due_date || item.requested_completion_date} />
          </div>
        </div>

        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
          <span>{item.person_in_content ? `👤 ${item.person_in_content}` : (item.talent_required ? `👤 ${item.talent_required}` : '—')}</span>
          <StatusPill status={item.status} size="sm" />
        </div>
      </div>
    </button>
  );
}
